import { generatePkcePair } from '../auth/PkceUtils';
import { TokenStore } from '../auth/TokenStore';
import { AuthService } from '../auth/AuthService';
import { AppConfig } from '../config/AppConfig';

export type LoginStartOptions = {
  /** SPA path to return to after successful auth (e.g., '/'). */
  returnPath?: string;
  /** Optional OAuth prompt parameter (e.g., 'login' to force re-auth). */
  prompt?: 'login' | 'consent' | 'none';
};

export type LoginStartResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'PKCE_GENERATION_FAILED' | 'PERSIST_FAILED' | 'URL_BUILD_FAILED';
      error?: string;
    };

export async function startLogin(options?: LoginStartOptions): Promise<LoginStartResult> {
  try {
    const { verifier, challenge, state } = await generatePkcePair();

    try {
      TokenStore.setPendingLogin({
        verifier,
        state,
        returnPath: options?.returnPath ?? '/',
        createdAt: Date.now(),
      });
    } catch (e) {
      return {
        ok: false,
        reason: 'PERSIST_FAILED',
        error: e instanceof Error ? e.message : 'Storage error',
      };
    }

    let authorizeUrl: string;
    try {
      const config = AppConfig.get();
      authorizeUrl = AuthService.buildAuthorizeUrl({
        redirectUri: config.cognito.redirectUri,
        state,
        codeChallenge: challenge,
        prompt: options?.prompt,
      });
    } catch (e) {
      return {
        ok: false,
        reason: 'URL_BUILD_FAILED',
        error: e instanceof Error ? e.message : 'URL build error',
      };
    }

    location.assign(authorizeUrl);
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      reason: 'PKCE_GENERATION_FAILED',
      error: e instanceof Error ? e.message : 'PKCE generation failed',
    };
  }
}

export function clearPendingLogin(): void {
  TokenStore.clearPendingLogin();
}
